export interface IFetchError {
  error?: any;
}

export type GraphErrorCode =
  | "accessDenied"
  | "activityLimitReached"
  | "extensionError"
  | "generalException"
  | "invalidRange"
  | "invalidRequest"
  | "itemNotFound"
  | "malwareDetected"
  | "nameAlreadyExists"
  | "notAllowed"
  | "notSupported"
  | "resourceModified"
  | "resyncRequired"
  | "serviceNotAvailable"
  | "syncStateNotFound"
  | "quotaLimitReached"
  | "unauthenticated"
  | "userContentMigrated";

export interface IGraphInnerError {
  code: string | GraphErrorCode;
  date?: string;
  innererror?: IGraphInnerError;
  "request-id"?: string;
}

export interface IGraphError {
  error: {
    code: string | GraphErrorCode;
    innererror?: IGraphInnerError;
    message: string;
  };

  statusCode: number;
}

/**
 * An INetworkError represents the general form of errors that can be returned.
 * Right now, only an IGrpahError can be returned.
 */
export type INetworkError = IFetchError | IGraphError;

export function graphBlob(response: Response): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    if (response.ok) {
      response.blob().then(resolve, reject);
    } else {
      reject(response);
    }
  });
}

export function graphImage(response: Response): Promise<Blob | null> {
  return new Promise<Blob | null>((resolve, reject) => {
    if (response.ok) {
      response.blob().then(resolve, reject);
    } else if (response.status === 404) {
      resolve(null);
    } else {
      reject(response);
    }
  });
}

export function graphJSON<T>(response: Response): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    if (response.ok) {
      const contentType = response.headers.get("content-type");

      if (contentType && /application\/json/.test(contentType)) {
        response.json().then(resolve, reject);
      } else {
        reject(response);
      }
    } else {
      reject(response);
    }
  });
}

export function graphNoContent(response: Response): Promise<void> {
  return new Promise((resolve, reject) => {
    if (response.ok && response.status === 204) {
      resolve();
    } else {
      reject(response);
    }
  });
}

/**
 * Given a raw URL make the request and process the response with the given
 * response handler.
 *
 * @param fetch The fetch method used to make the network request
 * @param url The URL to execute the request against.
 * @param init Optional set of fetch options used to create the network request.
 * @params responseHandler The response handler to process the resposne. This either
 * uses the graphJSON handler by default.
 * @returns A Promise<T> created via the network request and response processing.
 */
export function graphRequest<T>(
  fetch: (url: string, init?: RequestInit) => Promise<Response>,
  url: string,
  init?: RequestInit,
  responseHandler: (response: Response) => Promise<T> = graphJSON
): Promise<T> {
  if (init) {
    return fetch(url, init).then(responseHandler);
  } else {
    return fetch(url).then(responseHandler);
  }
}

/**
 * graphResponse is used to process a fetch response and either returned
 * the json deserialized object, or reject with an error. This will be some form
 * of core networking error, deserialization error, or an INetworkError.
 *
 * @param responsePromise The promise that resulted from the fetch request.
 * @param responseHandler The response handler to process the resposne. This either
 * uses the graphJSON handler by default.
 * @returns A Promise<T> created via the network request and response processing.
 */
export function graphResponse<T>(responsePromise: Promise<Response>, responseHandler: (response: Response) => Promise<T> = graphJSON): Promise<T> {
  return responsePromise.then(responseHandler);
}

/**
 * Given a network failure this method will attempt to produce an error that
 * best matches the result. This means it will use the standard Graph pattern
 * to prepare the results.
 *
 * @param response Network response.
 * @returns Promise of the the network error.
 */
export function parseNetworkError(response: Response): Promise<INetworkError> {
  const contentType = response.headers.get("content-type");
  const statusCode = response.status;

  // For errors in the 400 - 599 range we will extract the body
  // and use it as the result.
  if (response.status >= 400 && response.status <= 599) {
    if (contentType && /application\/json/.test(contentType)) {
      if (!response.bodyUsed) {
        return response
          .clone()
          .json()
          .then((error: any) => {
            return { ...error, statusCode };
          })
          .catch((error: any) => {
            return { error: { code: "parseError", message: error.message }, statusCode };
          });
      } else {
        return Promise.resolve({ error: { code: "bodyUsed", message: "Unable to retrieve response details" }, statusCode });
      }
    } else {
      const clientErrorCode = response.headers.get("x-clienterrorcode");
      const errorCodeChain = response.headers.get("x-errorcodechain");
      const internalErrorCode = response.headers.get("x-internalerrorcode");
      const contentLength = response.headers.get("content-length");
      return Promise.resolve({
        error: {
          code: clientErrorCode || "nonJsonError",
          message: `Unexpected content type ${contentType}, length ${contentLength}, errorCodeChain ${errorCodeChain}, internalErrorCode ${internalErrorCode}`
        },
        statusCode
      });
    }
  } else {
    return Promise.resolve({
      error: { code: "unexpectedStatus", message: `Unexpected network response ${contentType}` },
      statusCode
    });
  }
}
